import { LocationSharingApi } from '@deckee/api-app-main';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { AppAsyncThunkAPI } from '../..';
import apiConfiguration from '../../../backend/apiConfiguration';
import { moveMapToLocation } from '../app-controls';
import { selectCurrentMemberId } from '../backend-service';
import { Signal, SignalShare } from './models';
import { PositionDataResponse } from './signal.types';

export const api = new LocationSharingApi(apiConfiguration);

export const loadingSignalShares = createAction(
  '[Signal] Loading signals shares',
  (isLoading: boolean) => ({ payload: isLoading }),
);

export const updateSignalById = createAction(
  '[Signal] update signal by id',
  (id: string, payload: Signal) => ({
    payload: {
      ...payload,
      id,
    },
  }),
);

export const addSignals = createAction(
  '[Signal] add signals',
  (data: PositionDataResponse[]) => ({
    payload: data.reduce(
      (acc, item) => ({
        ...acc,
        [item.id]: item,
      }),
      {},
    ),
  }),
);

export const setSignals = createAction(
  '[Signal] set signals',
  (data: PositionDataResponse[]) => ({
    payload: data.reduce(
      (acc, item) => ({
        ...acc,
        [item.id]: item,
      }),
      {},
    ),
  }),
);

export const removeShare = createAction(
  '[Signal] Remove share from store',
  (shareId: string) => ({
    payload: shareId,
  }),
);

export const removeShareFromMember = createAsyncThunk<
  boolean,
  string,
  AppAsyncThunkAPI
>(
  '[Signal] Remove signal share from member',
  async (shareId, { dispatch, rejectWithValue }) => {
    // Request the share
    try {
      await api.deleteShare({
        id: shareId,
      });

      dispatch(removeShare(shareId));
      return true;
    } catch (e: any) {
      return rejectWithValue(e as Error);
    }
  },
);

export const setSignalShares = createAction(
  '[Signal] Set signal shares',
  (shares: SignalShare[]) => {
    // The payload should be the array of shares converted to a dictionary
    const sharesDictionary = shares.reduce(
      (acc, share) => ({
        ...acc,
        [share.id]: share,
      }),
      {} as { [key: string]: SignalShare },
    );

    return {
      payload: sharesDictionary,
    };
  },
);

/**
 * Gets the share records shared by the current member
 *
 * i.e. People the current user has shared their own location with
 */
export const getSharedSignalsByCurrentMember = createAsyncThunk<
  boolean,
  void,
  {
    rejectValue: unknown;
  }
>(
  '[Signal] Get shared signals by current member',
  async (_, { getState, dispatch, rejectWithValue }) => {
    // Get the current logged in member
    const memberId = selectCurrentMemberId(getState());

    // Request the share
    try {
      const { data } = await api.getShares({
        sharedBy: `${memberId}`,
      });
      const response = data;

      dispatch(setSignalShares(response));
      return true;
    } catch (e: unknown) {
      return rejectWithValue(e);
    }
  },
);

/**
 * Gets the share records shared with the current member
 *
 * i.e. People who have shared their location WITH the current member
 */
export const getSharedSignalsWithCurrentMember = createAsyncThunk<
  boolean,
  void,
  {
    rejectValue: unknown;
  }
>(
  '[Signal] Get shared signals with current member',
  async (_, { getState, dispatch, rejectWithValue }) => {
    dispatch(loadingSignalShares(true));

    // Get the current logged in member
    const memberId = selectCurrentMemberId(getState());

    // Request the share
    try {
      const { data } = await api.getShares({
        sharedWith: `${memberId}`,
      });

      dispatch(setSignalShares(data));
      dispatch(loadingSignalShares(false));
      return true;
    } catch (e: unknown) {
      dispatch(loadingSignalShares(false));
      return rejectWithValue(e);
    }
  },
);

export const shareSignalWithMember = createAsyncThunk<
  boolean,
  string,
  {
    rejectValue: unknown;
  }
>(
  '[Signal] Create shared signal with given member',
  async (shareWith, { getState, rejectWithValue }) => {
    // Get the current logged in member
    const memberId = selectCurrentMemberId(getState());

    // Request the share
    try {
      await api.createOrUpdateShare({
        createOrUpdateShareRequest: {
          sharedWith: shareWith,
          sharedBy: `${memberId}`,
        },
      });

      return true;
    } catch (e: unknown) {
      return rejectWithValue(e);
    }
  },
);

export const fetchSignal = createAsyncThunk<void, string>(
  '[Signal] fetch signal',
  async (id, { dispatch }) => {
    const { data } = await api.getPosition({
      id,
    });

    dispatch(updateSignalById(id, data));
  },
);

export const setSelectedSignal = createAction(
  '[Signal] Set selected signal',
  (id?: string) => ({
    payload: id,
  }),
);

export const fetchLoggedInMembersPositions = createAsyncThunk<
  Promise<void>,
  void,
  AppAsyncThunkAPI
>('[Signal] update signal for current member', async (_, { dispatch }) => {
  const { data } = await api.getMemberAndFriendsPositions();

  if (data?.length > 0) {
    dispatch(addSignals(data));
  }
});

export const moveMapToSignalsPosition = createAsyncThunk<
  void,
  { id: string; offsetX?: number; offsetY?: number; jumpToLocation: boolean },
  AppAsyncThunkAPI
>(
  '[Signal] Move map to signals position',
  (
    { id, offsetY, offsetX, jumpToLocation = false },
    { dispatch, getState },
  ) => {
    const state = getState();

    const position = state.signal.signals[id];

    dispatch(
      moveMapToLocation({
        center: {
          lat: position.latitude,
          lon: position.longitude,
        },
        offsetY,
        offsetX,
        jumpToLocation,
      }),
    );
  },
);

export const reset = createAction('[Signal] reset');

/**
 * Gets all the share records publicly shared
 *
 * i.e. People who have shared their location publicly
 */
export const getAllPublicSignals = createAsyncThunk<
  void,
  void,
  AppAsyncThunkAPI
>(
  '[Signal] Get all publicly shared signals',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      // Use global bounding box to return all public signals
      const globalBoundingBox = [
        {
          lat: 90,
          lon: -179,
        },
        {
          lat: -90,
          lon: 180,
        },
      ];

      const { data: results } = await api.getPositions({
        getPositionsRequest: {
          boundingBox: globalBoundingBox,
        },
      });
      dispatch(setSignals(results));
    } catch (error: unknown) {
      rejectWithValue(error);
    }
  },
);
